// Extracted from web/App.tsx; behavior and declaration bodies preserved.
import { ArrowRight, ArrowUpFromLine, BookOpen, ChevronRight, FilePlus2, Folder, Network, Plus, Shield } from "lucide-react";

import type { ModelSummary, Repository, Session } from "../../shared/contracts";

import ModelTypeIcon from "../components/ModelTypeIcon";
import { Button, modelTypeLabel } from "../components/Primitives";

export function Overview({
  session,
  models,
  repositories,
  onOpen,
  onCreate,
  onImport,
  onHelp,
  canEdit,
}: {
  session: Session;
  models: ModelSummary[];
  repositories: Repository[];
  onOpen: (id: string) => void;
  onCreate: () => void;
  onImport: () => void;
  onHelp: () => void;
  canEdit: boolean;
}) {
  return (
    <div className="overview">
      <div className="overview-top">
        <div>
          <span className="eyebrow">YOUR MODELING WORKSPACE</span>
          <h1>
            Good design starts
            <br />
            with a clear picture.
          </h1>
          <p>Connect business meaning to the systems that hold it.</p>
        </div>
        <div className="overview-mark">
          <Network size={100} strokeWidth={0.7} />
        </div>
      </div>
      <div className="quick-start-grid">
        <button disabled={!canEdit} onClick={onCreate}>
          <span className="quick-icon">
            <FilePlus2 size={23} />
          </span>
          <div>
            <h3>Create a model</h3>
            <p>Start from an empty canvas.</p>
          </div>
          <ArrowRight size={17} />
        </button>
        <button disabled={!canEdit} onClick={onImport}>
          <span className="quick-icon blue">
            <ArrowUpFromLine size={23} />
          </span>
          <div>
            <h3>Bring in a schema</h3>
            <p>Inspect and import DDL or JSON.</p>
          </div>
          <ArrowRight size={17} />
        </button>
        <button onClick={onHelp}>
          <span className="quick-icon ochre">
            <BookOpen size={23} />
          </span>
          <div>
            <h3>Find your way</h3>
            <p>Practical, context-aware guidance.</p>
          </div>
          <ArrowRight size={17} />
        </button>
      </div>
      <div className="recent-heading">
        <h2>
          Recent models <span>{models.length}</span>
        </h2>
        <span>
          {repositories.length}{" "}
          {repositories.length === 1 ? "repository" : "repositories"}
        </span>
      </div>
      {models.length ? (
        <div className="models-table">
          <div className="models-table-head">
            <span>MODEL</span>
            <span>TYPE</span>
            <span>REPOSITORY</span>
            <span>LAST UPDATED</span>
          </div>
          {models
            .slice()
            .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
            .map((m) => (
              <button key={m.modelId} onClick={() => onOpen(m.modelId)}>
                <span className="table-model-name">
                  <span className="table-model-icon">
                    <ModelTypeIcon type={m.modelType} size={21} />
                  </span>
                  <strong>{m.name}</strong>
                </span>
                <span>
                  <span className="model-type-tag">
                    {modelTypeLabel(m.modelType)}
                  </span>
                </span>
                <span>
                  {repositories.find((r) => r.repositoryId === m.repositoryId)
                    ?.name || "Repository"}
                </span>
                <span>
                  {new Date(m.updatedAt).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })}
                  <ChevronRight size={15} />
                </span>
              </button>
            ))}
        </div>
      ) : (
        <div className="empty-library">
          <div>
            <Folder size={34} strokeWidth={1.2} />
            <h3>Your workspace is ready.</h3>
            <p>
              Create your first model or import an existing schema.
              <br />
              There are no sample models to clean up.
            </p>
            <Button
              variant="primary"
              icon={Plus}
              disabled={!canEdit}
              onClick={onCreate}
            >
              Create your first model
            </Button>
          </div>
        </div>
      )}
      <div className="overview-note">
        <Shield size={15} />
        <span>
          Drafts, saved revisions, publications, and database deployments remain
          distinct.
        </span>
        <span className="alpha-note">
          Development build · verify support per profile
        </span>
      </div>
    </div>
  );
}
